import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import styled from 'styled-components'
import { MetricCard } from '../../../../atoms/MetricCard'
import { usePurchaseMarket } from '../../PurchaseMarketContext'

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 16px;
  }
`

interface MarketSummaryMetricsProps {
  t: TFunction
}

interface SummaryData {
  medianPrice: number
  medianPriceM2: number
  totalListings: number
}

export const MarketSummaryMetrics: React.FC<MarketSummaryMetricsProps> = ({
  t,
}) => {
  const { filters, service } = usePurchaseMarket()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<SummaryData | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        // Calculate date range for last 12 months
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)

        const apiFilters = {
          deal_type: 'purchase',
          property_category:
            filters.propertyCategory.length > 0
              ? filters.propertyCategory
              : undefined,
          location: locationValue,
          location_type: locationType,
          active_listing_timestamp: {
            min: startDate.toISOString().split('T')[0],
            max: endDate.toISOString().split('T')[0],
          },
          outlier_field: 'price',
          outlier_percentage: 2,
        }

        // Fetch distribution data to get summary metrics
        const response = await service.getDistributionData(apiFilters)
        const aggs = response.aggregations

        // Extract median price from price percentiles
        const medianPrice =
          aggs?.price_percentiles?.values?.['50.0'] ||
          aggs?.price_stats?.avg ||
          0

        // Extract median price/m² from price_m2 percentiles
        const medianPriceM2 =
          aggs?.price_m2_percentiles?.values?.['50.0'] ||
          aggs?.price_m2_stats?.avg ||
          0

        // Get total listings count from rooms histogram buckets
        const roomsBuckets = aggs?.rooms_histogram?.buckets || []
        const totalListings = roomsBuckets.reduce(
          (sum: number, b: { doc_count: number }) => sum + b.doc_count,
          0
        )

        setData({
          medianPrice,
          medianPriceM2,
          totalListings,
        })
      } catch (err) {
        console.error('Error fetching market summary:', err)
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters, service])

  const formatPriceM2 = (value: number): string =>
    `CHF ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`

  const formatPrice = (value: number): string => {
    if (value >= 1000000) {
      return `CHF ${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `CHF ${Math.round(value / 1000)}k`
    }
    return `CHF ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }

  if (loading || !data) {
    return (
      <MetricsGrid>
        <MetricCard
          title={t('summary.medianPriceM2.title')}
          value="—"
          subtitle={t('summary.medianPriceM2.subtitle')}
          variant="default"
        />
        <MetricCard
          title={t('summary.medianPrice.title')}
          value="—"
          subtitle={t('summary.medianPrice.subtitle')}
          variant="default"
        />
        <MetricCard
          title={t('summary.marketVolume.title')}
          value="—"
          subtitle={t('summary.marketVolume.subtitle')}
          variant="default"
        />
      </MetricsGrid>
    )
  }

  return (
    <MetricsGrid>
      <MetricCard
        title={t('summary.medianPriceM2.title')}
        value={formatPriceM2(data.medianPriceM2)}
        subtitle={t('summary.medianPriceM2.subtitle')}
        variant="default"
      />
      <MetricCard
        title={t('summary.medianPrice.title')}
        value={formatPrice(data.medianPrice)}
        subtitle={t('summary.medianPrice.subtitle')}
        variant="default"
      />
      <MetricCard
        title={t('summary.marketVolume.title')}
        value={data.totalListings.toLocaleString()}
        subtitle={t('summary.marketVolume.subtitle')}
        variant="default"
      />
    </MetricsGrid>
  )
}
